import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { Stack } from '@atlaskit/primitives/compiled';
import Select from '@atlaskit/select';
import { ErrorMessage, Label } from '@atlaskit/form';
import { IconButton } from '@atlaskit/button/new';
import DeleteIconMigration from '@atlaskit/icon/core/delete';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';

import { messages } from '../../messages';
import type {
	Column,
	UnsavedColumnToFieldMapping,
} from '../../providers/BulkConfigureContextProvider';
import {
	useMappingsActions,
	useMappingsState,
	useIssueTypesState,
} from '../../providers/BulkConfigureContextProvider';
import {
	FieldMetadataSchemaType,
	type FieldMetadata,
	SimpleJiraIssuesSet,
} from '../../__types__/apiUtils';
import type { PageMode } from '../../providers/IssueCreateSidePanelContextProvider';
import { useIssueCreateSidePanelContext } from '../../providers/IssueCreateSidePanelContextProvider';
import {
	getFieldTypeFromMetadata,
	getValidatedIssueFieldValuesForColumn,
} from '../../utils/tableUtils';

interface MappingOption<T extends Column | FieldMetadata> {
	label: string;
	value: T;
}

export interface ColumnToIssueFieldMappingProps {
	index: number;
	mapping: UnsavedColumnToFieldMapping;
	tableColumnOptions: MappingOption<Column>[];
	issueFieldOptions: MappingOption<FieldMetadata>[];
	onMappingChange: (index: number, newMapping: UnsavedColumnToFieldMapping) => void;
	isLoading: boolean;
	isDisabled: boolean;
	pageMode: PageMode | undefined;
	onAutoScrollOnMenuOpen?: () => void;
}

export const TABLE_COLUMN_SELECT_ID = 'table-column-select';
export const ISSUE_FIELD_SELECT_ID = 'issue-field-select';

// Components do not have FieldMetadata the way regular issue fields do, so we define a special metadata object for it.
export const ComponentsFieldMetadata: FieldMetadata = {
	fieldId: 'components',
	hasDefaultValue: false,
	key: 'components',
	name: 'Components',
	operations: [],
	schema: { type: FieldMetadataSchemaType.COMPONENTS },
	// We will only show the Components field if the entire column is valid.
	required: true,
};

export const ColumnToIssueFieldMapping = ({
	index,
	mapping,
	tableColumnOptions,
	issueFieldOptions,
	onMappingChange,
	isLoading,
	isDisabled,
	pageMode,
	onAutoScrollOnMenuOpen,
}: ColumnToIssueFieldMappingProps) => {
	const { deleteUnsavedColumnToFieldMapping } = useMappingsActions();

	const handleMenuOpen = useCallback(() => {
		onAutoScrollOnMenuOpen?.();
	}, [onAutoScrollOnMenuOpen]);

	const handleSelectChange = useCallback(
		(idx: number, key: 'column' | 'field', newValue: any) => {
			if (newValue) {
				onMappingChange(idx, { ...mapping, [key]: newValue.value });
			}
		},
		[mapping, onMappingChange],
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const deleteMapping = useCallback(
		(idx: number) => {
			if (isLoading) {
				return;
			}
			deleteUnsavedColumnToFieldMapping(idx);
		},
		[isLoading, deleteUnsavedColumnToFieldMapping],
	);

	const [{ selectedTable }] = useIssueCreateSidePanelContext();
	const {
		mappingWorkItemTypes,
		mappingValidParentCandidates,
		mappingFieldAllowedValues,
		mappingAvailableComponents,
	} = useMappingsState();
	const { selectedIssueType } = useIssueTypesState();

	const { formatMessage } = useIntl();

	const availableIssueFieldOptions = useMemo(() => {
		const allIssueFieldOptions = [...issueFieldOptions];
		if (mappingAvailableComponents && mappingAvailableComponents.length > 0) {
			allIssueFieldOptions.push({
				label: formatMessage(
					messages.bulkCreateConfigureBodyWorkFieldMappingsComponentsFieldOption,
				),
				value: ComponentsFieldMetadata,
			});
		}
		return allIssueFieldOptions;
	}, [issueFieldOptions, mappingAvailableComponents, formatMessage]);

	// Includes the components allowed values if they are available.
	const fullMappingFieldAllowedValues: Map<string, unknown[]> = useMemo(() => {
		const map = new Map(mappingFieldAllowedValues ?? []);
		if (mappingAvailableComponents && mappingAvailableComponents.length > 0) {
			map.set('components', mappingAvailableComponents);
		}
		return map;
	}, [mappingFieldAllowedValues, mappingAvailableComponents]);

	const validatedIssueFieldOptions = useMemo(() => {
		return availableIssueFieldOptions.filter((option) => {
			if (!mapping.column) {
				return false;
			}

			const validatedIssueFieldValues = getValidatedIssueFieldValuesForColumn(
				mapping.column,
				option.value.fieldId,
				selectedTable ?? new HTMLTableElement(),
				{
					mappingWorkItemTypes: mappingWorkItemTypes ?? new Map(),
					selectedIssueType,
					mappingValidParentCandidates: mappingValidParentCandidates ?? new SimpleJiraIssuesSet(),
					mappingFieldAllowedValues: fullMappingFieldAllowedValues,
				},
			);

			const checkedType = getFieldTypeFromMetadata(option.value);
			const validatedValues = validatedIssueFieldValues?.get(checkedType);

			// If the field is required, it must be canBeRequired. Otherwise, it must have at least one validated value.
			return (
				validatedValues != null &&
				(option.value.required
					? validatedValues.canBeRequired
					: validatedValues.fieldValues.length > 0)
			);
		});
	}, [
		mapping,
		mappingWorkItemTypes,
		mappingValidParentCandidates,
		fullMappingFieldAllowedValues,
		selectedIssueType,
		selectedTable,
		availableIssueFieldOptions,
	]);

	const fireDeleteMappingAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'confluenceHighlightActionsMenu',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'deleteMapping',
				type: 'ui',
				attributes: {
					pageMode,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, pageMode]);

	const handleDeleteMappingClick = useCallback(
		(idx: number) => {
			fireDeleteMappingAnalytics();
			deleteMapping(idx);
		},
		[fireDeleteMappingAnalytics, deleteMapping],
	);

	return (
		<Stack space="space.100">
			<Stack>
				<Label htmlFor="table-column-select">
					<FormattedMessage {...messages.bulkConfigureMapTableToIssueTableColumn} />
				</Label>
				<div>
					<Select
						inputId={TABLE_COLUMN_SELECT_ID}
						classNamePrefix={TABLE_COLUMN_SELECT_ID}
						appearance="default"
						options={tableColumnOptions}
						defaultValue={tableColumnOptions.find((option) => option.value === mapping.column)}
						onChange={(newValue) => handleSelectChange(index, 'column', newValue)}
						onMenuOpen={handleMenuOpen}
						isLoading={isLoading}
						isDisabled={isDisabled}
					/>
				</div>
				{mapping.errors.column && <ErrorMessage> {mapping.errors.column.message} </ErrorMessage>}
			</Stack>
			<Stack>
				<Label htmlFor="issue-field-select">
					{fg('confluence-issue-terminology-refresh') ? (
						<FormattedMessage
							{...messages.bulkConfigureMapTableToIssueIssueFieldIssueTermRefresh}
						/>
					) : (
						<FormattedMessage {...messages.bulkConfigureMapTableToIssueIssueField} />
					)}
				</Label>
				<div>
					<Select
						inputId={ISSUE_FIELD_SELECT_ID}
						classNamePrefix={ISSUE_FIELD_SELECT_ID}
						appearance="default"
						options={validatedIssueFieldOptions}
						defaultValue={validatedIssueFieldOptions.find(
							(option) => option.value === mapping.field,
						)}
						onChange={(newValue) => handleSelectChange(index, 'field', newValue)}
						onMenuOpen={handleMenuOpen}
						isLoading={isLoading}
						isDisabled={isDisabled || mapping.isDefaultMapping}
					/>
				</div>
				{mapping.errors.field && <ErrorMessage> {mapping.errors.field.message} </ErrorMessage>}
			</Stack>
			{!mapping.isDefaultMapping && (
				<Stack alignInline="end">
					<IconButton
						appearance="subtle"
						icon={DeleteIconMigration}
						onClick={() => handleDeleteMappingClick(index)}
						isDisabled={isDisabled}
						label="delete"
						testId="delete-additional-mapping"
					/>
				</Stack>
			)}
		</Stack>
	);
};
